import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Trash2, Check } from "lucide-react";

export default function SignaturePad({ label, value, onChange }) {
  const sigRef = useRef(null);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(300);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth - 4);
    }
    const handleResize = () => {
      if (containerRef.current) setWidth(containerRef.current.offsetWidth - 4);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Restore saved signature when value changes externally
  useEffect(() => {
    if (value && sigRef.current && sigRef.current.isEmpty()) {
      sigRef.current.fromDataURL(value, { width, height: 120 });
    }
  }, [value, width]);

  const handleEnd = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      onChange(dataUrl);
    }
  };

  const handleClear = () => {
    if (sigRef.current) sigRef.current.clear();
    onChange("");
  };

  return (
    <div ref={containerRef} data-testid={`signature-pad-${(label || "").replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>
          {label || "Signature"}
        </label>
        {value && (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600">
            <Check className="w-3 h-3" /> Signed
          </span>
        )}
      </div>
      <div className="relative rounded-xl border-2 border-dashed overflow-hidden" style={{ borderColor: value ? '#22C55E' : 'var(--border-main)', backgroundColor: 'var(--bg-input)' }}>
        <SignatureCanvas
          ref={sigRef}
          penColor="#0A0A0A"
          canvasProps={{
            width,
            height: 120,
            className: "signature-canvas",
            style: { touchAction: "none", cursor: "crosshair" },
          }}
          onEnd={handleEnd}
        />
        <div className="absolute bottom-2 left-3 right-3 border-b border-dotted" style={{ borderColor: 'var(--border-light)' }} />
        <p className="absolute bottom-0.5 left-3 text-[9px]" style={{ color: 'var(--text-muted)' }}>Sign above the line</p>
        {value && (
          <button onClick={handleClear} data-testid="clear-signature"
            className="absolute top-2 right-2 p-1.5 rounded-lg transition-colors hover:bg-red-50" style={{ color: '#EF4444' }}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
